import { useQuery } from '@tanstack/react-query'
import type { Money } from '@/shared/lib/money'
import { cashdeskKeys } from './cashdeskKeys'
import {
  fetchCashierAgents,
  fetchCashierBalance,
  fetchPerTxnLimit,
  type CashierAgent,
  type CashierBalance,
} from './cashdeskApi'

export interface CashierState {
  /** Tanlangan tugun; `null` — hali aniqlanmagan yoki umuman yo'q */
  agent: CashierAgent | null
  /** FAOL tugunlar. Kassirda bitta, adminda bir necha bo'lishi mumkin */
  agents: readonly CashierAgent[]
  balance: CashierBalance | null
  /**
   * Bitta to'lovning shifti; `null` — chegara yo'q yoki hali noma'lum.
   *
   * ⚠️ VALYUTA BALANSDAN olinadi: `fund-limits` yalang'och minor son
   * qaytaradi. Balans kelmaguncha shift `null` bo'lib turadi — taxminiy
   * valyuta bilan taqqoslash summani boshqa kattalikda o'lchardi va
   * ogohlantirish YOLG'ON bo'lardi.
   */
  perTxnLimit: Money | null
  loading: boolean
  /** Depozit so'rovi yiqildi — banner shu bilan chiziladi */
  error: unknown
  /** FAOL tugun umuman yo'q — kassa ishlay olmaydi */
  noActiveAgent: boolean
}

const NO_AGENTS: readonly CashierAgent[] = []

/**
 * Kassirning o'zi va uning depoziti.
 *
 * ⚠️ DEPOZIT DOIM SO'RALADI, kartochka ochiq bo'lishidan qat'i nazar.
 * Kassirning ishi u tugagan zahoti to'xtaydi va buni AMALDAN OLDIN
 * bilishi kerak: son ekranda turmasa, u yetmasligini faqat rad javobidan
 * — ya'ni abonent qarshisida — bilib olardi.
 *
 * `refetchInterval` YO'Q: depozit faqat kassirning o'z amallaridan va
 * administrator to'ldirishidan o'zgaradi. Birinchisini `useCashOperation`
 * bekor qiladi, ikkinchisi esa kunda bir-ikki marta bo'ladi va uni
 * so'rash uchun har o'n soniyada tarmoqni bezovta qilish ortiqcha.
 */
export function useCashier(selectedAgentId: string | null): CashierState {
  const agentsQuery = useQuery({
    queryKey: cashdeskKeys.agents,
    queryFn: fetchCashierAgents,
    staleTime: 5 * 60_000,
  })

  const agents = agentsQuery.data ?? NO_AGENTS
  const chosen =
    agents.find((a) => a.id === selectedAgentId) ?? (agents.length === 1 ? agents[0] : undefined)
  const agent = chosen ?? null

  const balanceQuery = useQuery({
    queryKey: cashdeskKeys.balance(agent?.id ?? ''),
    queryFn: () => fetchCashierBalance(agent?.id ?? ''),
    enabled: agent !== null,
  })

  // ⚠️ `retry: false` va XATOSI YUTILADI: shift kassaning ishlashiga
  // shart emas — u faqat rad javobini oldinroq aytadi. So'rov yiqilsa
  // ogohlantirish ko'rsatilmaydi, amalning o'zi esa serverda baribir
  // tekshiriladi.
  const perTxnQuery = useQuery({
    queryKey: cashdeskKeys.perTxnLimit(agent?.id ?? ''),
    queryFn: () => fetchPerTxnLimit(agent?.id ?? ''),
    enabled: agent !== null,
    retry: false,
    staleTime: 5 * 60_000,
  })

  const currency = balanceQuery.data?.balance.currency ?? null
  const perTxnMinor = perTxnQuery.data ?? null

  return {
    agent,
    agents,
    balance: balanceQuery.data ?? null,
    perTxnLimit:
      perTxnMinor === null || currency === null
        ? null
        : { amount: perTxnMinor, currency },
    loading: agentsQuery.isPending || (agent !== null && balanceQuery.isPending),
    error: agentsQuery.error ?? balanceQuery.error,
    noActiveAgent: agentsQuery.isSuccess && agents.length === 0,
  }
}
